import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Hidden, Typography } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../authentication/useAuth';
import useIdleTimeout from '../../hooks/useIdleTimeout';
import PasswordPrompt from '../../PasswordPrompt';
import { setAllSettings, setDrawerOpen } from '../../store/global';
import instance from '../../utils/imsAxios';
import SiteSelection from '../../views/Login/siteSelection';
import EditSettingsModal from '../../views/Operations/JobCards/EditSettingsModal';
import NavBar from './NavBar';
import TopBar from './TopBar';
import { getAllPendingUploads } from './TopBar/slice';
import { tokens } from '../../theme';

const MainLayout = () => {
    const dispatch = useDispatch();
    const { companyId, logout, user, canView } = useAuth();
    // Users who cannot see prices are never sent CCIP pricing, so its age is not theirs to warn about
    const seesPrices = canView('job-card-pricing');
    const { drawerOpen } = useSelector(state => state.global);
    const [openModal, setOpenModal] = React.useState(false);
    const hasShownStaleCcipWarning = React.useRef(false);
    const [ccipWarningOpen, setCcipWarningOpen] = React.useState(false);
    const [ccipWarningMessage, setCcipWarningMessage] = React.useState('');
    const [jobCardSettingsOpen, setJobCardSettingsOpen] = React.useState(false);
    const isAdmin = user?.roleDescription === 'Administrator' || user?.role === 'Administrator';

    // Why the password box is open: 'idle' (15 minutes without activity) or 'limit' (the 12 hours are up)
    const handleIdle = (reason) => {
        setOpenModal(reason);
    }

    const { idleTimer } = useIdleTimeout({ onIdle: handleIdle });

    const stay = () => {
        setOpenModal(false);
        idleTimer.reset();
    }

    const handleLogout = () => {
        logout();
        setOpenModal(false);
    }

    // Unsent-changes count, refreshed every minute; dispatching it during render sent a request on every redraw
    React.useEffect(() => {
        dispatch(getAllPendingUploads(companyId));
        const timer = setInterval(() => dispatch(getAllPendingUploads(companyId)), 60 * 1000);
        return () => clearInterval(timer);
    }, [companyId, dispatch]);

    React.useEffect(() => {
        const checkDailyCcipPricing = async () => {
            if (!companyId || !user?._id || !seesPrices || hasShownStaleCcipWarning.current) return;
            try {
                const { data } = await instance.get('company/settings', { params: { companyId } });
                const allSettings = data?.[0] ?? {};
                dispatch(setAllSettings(allSettings));
                const updatedAt = allSettings?.ccipPricing?.updatedAt;
                const updatedAtMs = updatedAt ? new Date(updatedAt).getTime() : NaN;
                const isStale = !Number.isFinite(updatedAtMs) || (Date.now() - updatedAtMs > 24 * 60 * 60 * 1000);
                if (isStale) {
                    const isAdmin = user?.roleDescription === 'Administrator' || user?.role === 'Administrator';
                    const message = isAdmin
                        ? 'CCIP daily pricing has not been updated in the last 24 hours. Please update it in Job Card Settings.'
                        : 'CCIP daily pricing has not been updated in the last 24 hours. Please notify an administrator.';
                    setCcipWarningMessage(message);
                    setCcipWarningOpen(true);
                    hasShownStaleCcipWarning.current = true;
                }
            } catch (error) {
                // Keep silent if settings cannot be loaded; existing page flows already handle settings errors.
            }
        };

        checkDailyCcipPricing();
    }, [companyId, user?._id, user?.roleDescription, user?.role, seesPrices, dispatch]);

    return (
        <>
            <PasswordPrompt open={!!openModal} reason={openModal} stay={stay} handleLogout={handleLogout} />
            <EditSettingsModal open={jobCardSettingsOpen} setOpen={setJobCardSettingsOpen} />
            <Dialog
                open={ccipWarningOpen}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    setCcipWarningOpen(false);
                }}
            >
                <DialogTitle>CCIP Daily Pricing Update Required</DialogTitle>
                <DialogContent>
                    <Typography>{ccipWarningMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    {
                        isAdmin ?
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => {
                                    setCcipWarningOpen(false);
                                    setJobCardSettingsOpen(true);
                                }}
                            >
                                Update Now
                            </Button>
                            : null
                    }
                    {
                        !isAdmin ?
                            <Button variant="contained" onClick={() => setCcipWarningOpen(false)}>
                                I Understand
                            </Button>
                            : null
                    }
                </DialogActions>
            </Dialog>
            {
                !!localStorage.getItem('selectedSite') ? 
                    <div
                        style={{
                            display: 'flex',
                            height: '100%',
                            overflow: 'hidden',
                            width: '100%',
                        }}
                    > 
                        <TopBar/>
                        <Hidden mdDown >
                            <NavBar/>
                        </Hidden>
                        <Drawer 
                            open={drawerOpen}  
                            ModalProps={{ onBackdropClick: () => dispatch(setDrawerOpen()) }}
                        >
                            <NavBar />
                        </Drawer>
                        <div
                            style={{
                                display: 'flex',
                                flex: '1 1 auto',
                                overflow: 'hidden',
                                paddingTop: 64,
                                height: '100vh',
                                backgroundColor: tokens.bg,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: '1 1 auto',
                                    overflow: 'hidden',
                                    height: '100%',
                                    width: '100%',
                                    padding: 16,
                                }}
                            >
                                <Outlet />
                            </div>
                        </div>
                    </div>
                
                : <SiteSelection/>
            }
        </>
    )
};

export default MainLayout;
